import type { User } from '@burmazon/shared';
import { ADMIN_ROLES } from '@burmazon/shared';
import axios, { type AxiosError } from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { apiPost, getAccessToken, setAccessToken } from '../lib/api';
import { queryClient } from '../lib/queryClient';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

const IDLE_MS = 30 * 60 * 1000; // 30 minutes
export const ACTIVITY_KEY = 'burmazon-admin-last-activity';

// Singleton guard: React StrictMode (and concurrent renders) can call
// restoreSession() twice simultaneously with the same refresh token.
// The second call would hit the Redis token-rotation "already rotated →
// revoke all sessions" path and log the user out. Returning the in-flight
// promise ensures only one /auth/refresh request is ever made at a time.
let restoringPromise: Promise<void> | null = null;

interface AdminAuthState {
  user: Omit<User, 'createdAt' | 'updatedAt'> | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  restoreSession: () => Promise<void>;
  updateUser: (patch: Partial<Omit<User, 'createdAt' | 'updatedAt'>>) => void;
}

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const res = await apiPost<{
            user: Omit<User, 'createdAt' | 'updatedAt'>;
            tokens: { accessToken: string; refreshToken: string };
          }>('/auth/login', { email, password });

          // Reject non-admin logins; users with a custom role are also allowed
          // since their permissions are controlled by the custom role itself
          if (
            !ADMIN_ROLES.includes(res.user.role as (typeof ADMIN_ROLES)[number]) &&
            !res.user.customRoleId
          ) {
            throw new Error('Access denied: Admin privileges required');
          }

          setAccessToken(res.tokens.accessToken);
          localStorage.setItem('admin-refreshToken', res.tokens.refreshToken);
          localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
          queryClient.clear();
          set({ user: res.user, isAuthenticated: true });
        } finally {
          set({ isLoading: false });
        }
      },

      updateUser: (patch) => {
        set((s) => ({ user: s.user ? { ...s.user, ...patch } : s.user }));
      },

      logout: async () => {
        const refreshToken = localStorage.getItem('admin-refreshToken');
        try {
          await apiPost('/auth/logout', { refreshToken });
        } catch {
          /* ignore */
        }
        setAccessToken(null);
        localStorage.removeItem('admin-refreshToken');
        localStorage.removeItem(ACTIVITY_KEY);
        queryClient.clear();
        set({ user: null, isAuthenticated: false });
      },

      restoreSession: () => {
        if (restoringPromise) return restoringPromise;

        restoringPromise = (async () => {
          // Already have a live access token — nothing to restore
          if (getAccessToken()) return;

          // Print tab: parent stored the token before window.open to avoid a
          // concurrent refresh call that would trigger Redis token-rotation revocation
          const printToken = localStorage.getItem('burmazon-print-token');
          if (printToken) {
            localStorage.removeItem('burmazon-print-token');
            setAccessToken(printToken);
            set({ isAuthenticated: true });
            return;
          }

          // 30-minute idle check: only enforce if lastActivity was previously recorded
          const lastActivity = localStorage.getItem(ACTIVITY_KEY);
          if (lastActivity && Date.now() - Number(lastActivity) > IDLE_MS) {
            localStorage.removeItem('admin-refreshToken');
            localStorage.removeItem(ACTIVITY_KEY);
            set({ user: null, isAuthenticated: false });
            return;
          }

          const refreshToken = localStorage.getItem('admin-refreshToken');
          if (!refreshToken) {
            set({ user: null, isAuthenticated: false });
            return;
          }
          try {
            const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
            const { accessToken, refreshToken: newRefresh } = data.data.tokens;
            setAccessToken(accessToken);
            localStorage.setItem('admin-refreshToken', newRefresh);
            set({ isAuthenticated: true });
          } catch (err) {
            const status = (err as AxiosError)?.response?.status;
            if (status === 401 || status === 403) {
              // Token truly invalid or revoked — clear the session
              setAccessToken(null);
              localStorage.removeItem('admin-refreshToken');
              localStorage.removeItem(ACTIVITY_KEY);
              set({ user: null, isAuthenticated: false });
            }
            // Network errors / 5xx: leave session intact so a transient outage
            // doesn't force the user to re-login
          }
        })().finally(() => {
          restoringPromise = null;
        });

        return restoringPromise;
      },
    }),
    {
      name: 'burmazon-admin-auth',
      partialize: (s) => ({ user: s.user, isAuthenticated: s.isAuthenticated }),
    },
  ),
);
